import {
  SESSIONS_TEST_IDS,
  type SessionLayer,
  type TurnLayer,
} from '@/components/admin/sessions/contracts';
import { sessionTime } from '@/lib/session-labels';
import type { SessionConversationBubble, SessionDetail } from '@matter_extractor/shared';
import { TriangleAlert } from 'lucide-react';
import { bubbleAnchor, bubbleShownLine, isBubbleFailed } from './conversation-fil';
import { LayerBubbleAppels, LayerBubbleBrut } from './layer-bubble';
import { MoreButton } from './turn-row';

/**
 * ONE page played in the conversation, under its band (prolongation.md §7, planche
 * `sessions.html`): the interviewer's row - the time, the persona's name, the
 * bubble and, in small print, what the page showed - then the person's row, which
 * is the CLICK.
 *
 * ## A bubble is not a turn, and the row says so by what it lacks
 *
 * No « Tour n » - the first column of the person's row says « Clic » - no scribe,
 * no objectives and no guardrails, no « directive reçue », and not one of the
 * arrow stops of the fil: a bubble is not a turn of the protocol (§4.8), and
 * giving it a number would shift the numbers a link carries (`?tour=`) and the
 * ones the farewell keeps. What it keeps of a turn is its « + » and the two layers
 * that still mean something, Appels and Brut.
 *
 * ## The click is what the person did, in the words of the screen
 *
 * `card` and the TEXT of the card taken - the text that became the person's
 * bubble in the reader - or `button` and its label. Which card was dealt first and
 * which was taken stay in the Parcours: this row says what was pressed, not how
 * the cards were ordered. A page left without a click says so in grey italics, as
 * the farewell turn says « aucune réponse attendue » - an empty cell would read
 * as an answer that is missing.
 *
 * ## A failed call is a line, not a blank
 *
 * §4.6: the call is not retried, and the page played WITHOUT a bubble. The row
 * is written all the same, with an empty message, so that the failure is read
 * HERE - in the yellow of every defect of the fil, with the provider's own reason
 * when the call recorded one. The Appels layer carries the same row of the
 * table, `échec : …`, that a failed call of a turn carries.
 */

export interface BubbleRowProps {
  bubble: SessionConversationBubble;
  detail: SessionDetail;
  /** The switches of the bar: a layer on is a layer open on every bubble. */
  openLayers: ReadonlySet<SessionLayer>;
  /** The « + » of this bubble was pressed: its two accordions exist, closed. */
  revealed: boolean;
  /** The folds a HAND changed, keyed `b<visite>:<couche>` across the whole screen. */
  folds: ReadonlyMap<string, boolean>;
  /** A counter while an arrival from the parcours lights this bubble, null otherwise. */
  flash: number | null;
  onReveal: () => void;
  onFold: (layer: TurnLayer, open: boolean) => void;
}

/** The key of a fold of a bubble in the screen's map - never a turn number's. */
export function bubbleFoldKey(visitIndex: number, layer: TurnLayer): string {
  return `b${visitIndex}:${layer}`;
}

export function BubbleRow({
  bubble,
  detail,
  openLayers,
  revealed,
  folds,
  flash,
  onReveal,
  onFold,
}: BubbleRowProps) {
  const personaName = detail.cascade.persona?.name ?? 'Interviewer';
  const failed = isBubbleFailed(bubble);
  const shown = bubbleShownLine(detail, bubble);
  const click = bubble.click;

  const layer = (which: 'appels' | 'brut') => {
    if (!revealed && !openLayers.has(which)) return null;
    const open = folds.get(bubbleFoldKey(bubble.visitIndex, which)) ?? openLayers.has(which);
    const onToggle = () => onFold(which, !open);
    return which === 'appels' ? (
      <LayerBubbleAppels key={which} bubble={bubble} open={open} onToggle={onToggle} />
    ) : (
      <LayerBubbleBrut
        key={which}
        sessionId={detail.session.id}
        bubble={bubble}
        open={open}
        onToggle={onToggle}
      />
    );
  };

  return (
    <li
      id={bubbleAnchor(bubble.visitIndex)}
      data-testid={SESSIONS_TEST_IDS.bubble}
      data-visit={bubble.visitIndex}
      data-page-id={bubble.pageId}
      data-failed={failed}
      data-click={click?.kind}
      data-flash={flash !== null}
      className="group relative scroll-mt-40 border-gray-100 border-b border-l-3 border-l-transparent last:border-b-0"
    >
      <div className="grid grid-cols-[3.5rem_minmax(0,1fr)] gap-3 px-5 py-3 sm:grid-cols-[3.5rem_6.5rem_minmax(0,1fr)] sm:gap-4">
        <time dateTime={bubble.createdAt} className="pt-0.5 text-gray-500 text-xs tabular-nums">
          {sessionTime(bubble.createdAt)}
        </time>
        <p className="font-semibold text-bleu-nuit-deep text-sm">{personaName}</p>
        <div className="col-span-2 min-w-0 sm:col-span-1">
          {failed ? (
            <p
              data-testid={SESSIONS_TEST_IDS.bubbleFailed}
              className="inline-flex flex-wrap items-center gap-1.5 rounded bg-jaune-tint px-2 py-0.5 font-semibold text-jaune-deep text-xs"
            >
              <TriangleAlert aria-hidden="true" className="size-3.5 shrink-0" />
              Appel en échec
              {bubble.call.failedReason === null ? '' : ` · ${bubble.call.failedReason}`} · la page
              s’est jouée sans bulle
            </p>
          ) : (
            <p className="whitespace-pre-wrap break-words text-noir">{bubble.message}</p>
          )}
          {shown === null ? null : (
            <p data-testid={SESSIONS_TEST_IDS.bubbleShown} className="mt-1.5 text-gray-600 text-xs">
              {shown}
            </p>
          )}
        </div>
      </div>

      <div className="relative grid grid-cols-[3.5rem_minmax(0,1fr)] gap-3 bg-gray-50 px-5 py-3 sm:grid-cols-[3.5rem_6.5rem_minmax(0,1fr)] sm:gap-4">
        <span className="self-start font-semibold text-gray-500 text-xs">Clic</span>
        <p className="text-gray-700 text-sm">Personne</p>
        <div className="col-span-2 min-w-0 pr-9 sm:col-span-1">
          {click === null ? (
            <p data-testid={SESSIONS_TEST_IDS.bubbleNoClick} className="text-gray-500 italic">
              aucun clic enregistré
            </p>
          ) : (
            <p
              data-testid={SESSIONS_TEST_IDS.bubbleClick}
              data-kind={click.kind}
              className="whitespace-pre-wrap break-words text-gray-900"
            >
              <span className="mr-1.5 inline-flex rounded border border-gray-200 bg-white px-1.5 align-[1px] font-semibold text-gray-700 text-micro uppercase leading-4 tracking-wide">
                {click.kind === 'card' ? 'carte' : 'bouton'}
              </span>
              {click.kind === 'card' ? (
                <span className="border-gray-300 border-l-3 pl-2.5 text-gray-800">
                  {click.text}
                </span>
              ) : (
                click.text
              )}
            </p>
          )}
        </div>
        <MoreButton
          number={bubble.visitIndex + 1}
          label={`Détails de la bulle de la page ${bubble.visitIndex + 1}`}
          testId={SESSIONS_TEST_IDS.bubbleMore}
          revealed={revealed}
          onReveal={onReveal}
        />
      </div>

      {layer('appels')}
      {layer('brut')}

      {flash === null ? null : (
        <span
          key={flash}
          aria-hidden="true"
          className="turn-flash pointer-events-none absolute inset-0"
        />
      )}
    </li>
  );
}
